import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail, ASPECT_RATIOS, indexById, refreshPosterSlides } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SlideShow } from "@/components/SlideShow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Film, Plus, Play, Pencil, Images, Loader2, Copy } from "lucide-react";

const padFor = (ratio) => ASPECT_RATIOS.find((r) => r.value === ratio)?.pad || "56.25%";
import { ProtectedDelete, LiveBadge, useLiveStatus } from "@/components/ProtectedDelete";

export default function Dashboard() {
  const { user } = useAuth();
  const isPrincipal = user?.role === "principal";
  const navigate = useNavigate();
  const [banners, setBanners] = useState(null);
  const live = useLiveStatus();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [ratio, setRatio] = useState("16:9");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const [b, pr] = await Promise.all([api.get("/banners"), api.get("/posters").catch(() => ({ data: [] }))]);
    const map = indexById(pr.data);
    setBanners(b.data.map((bn) => ({ ...bn, images: refreshPosterSlides(bn.images, map) })));
  };

  useEffect(() => {
    load();
  }, []);

  const createBanner = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/banners", { name: name.trim(), aspect_ratio: ratio, loop: true, images: [] });
      setOpen(false);
      setName("");
      toast.success("Bandeau créé");
      navigate(`/banner/${data.id}`);
    } catch {
      toast.error("Échec de la création");
    } finally {
      setCreating(false);
    }
  };

  const duplicate = async (b) => {
    try {
      const { data } = await api.post("/banners", {
        name: `${b.name} (copie)`, aspect_ratio: b.aspect_ratio, loop: b.loop, images: b.images || [],
      });
      toast.success("Bandeau dupliqué");
      navigate(`/banner/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de la duplication");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/banners/${id}`);
      setBanners((b) => b.filter((x) => x.id !== id));
      toast.success("Bandeau supprimé");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de la suppression");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="grain-overlay pointer-events-none fixed inset-0 opacity-60" />

      {/* header */}
      <header className="sticky top-11 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Film className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-extrabold tracking-tight">LuminaStream</h1>
              <p className="font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">
                {user?.email}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isPrincipal && <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-banner-button">
                  <Plus className="mr-1 h-4 w-4" /> Nouveau bandeau
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="create-banner-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display">Créer un bandeau</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>Nom du bandeau</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Ex. Accueil magasin"
                      data-testid="banner-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Format d'affichage</Label>
                    <Select value={ratio} onValueChange={setRatio}>
                      <SelectTrigger data-testid="banner-ratio-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ASPECT_RATIOS.map((r) => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={createBanner} disabled={creating} data-testid="confirm-create-banner-button">
                    {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Créer
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>}
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Mes bandeaux</h2>
          <p className="text-sm text-muted-foreground">Gérez vos diaporamas, durées et transitions.</p>
        </div>

        {banners === null ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : banners.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-24 text-center">
            <Images className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="font-display text-lg font-semibold">Aucun bandeau pour le moment</p>
            <p className="mb-5 text-sm text-muted-foreground">Créez votre premier bandeau défilant.</p>
            {isPrincipal && (
              <Button onClick={() => setOpen(true)} data-testid="empty-create-button">
                <Plus className="mr-1 h-4 w-4" /> Créer un bandeau
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {banners.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="group overflow-hidden rounded-2xl border border-border bg-card shadow-lg transition-colors hover:border-primary/50"
                data-testid={`banner-card-${b.id}`}
              >
                <div className="relative w-full overflow-hidden bg-black" style={{ paddingTop: padFor(b.aspect_ratio) }}>
                  {b.images?.length ? (
                    <SlideShow images={b.images} loop showProgress={false} />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground font-mono-label">
                      Vide
                    </div>
                  )}
                  <div className="absolute right-2 top-2 z-20 flex gap-1">
                    {live.banner_id === b.id && <LiveBadge testId={`live-badge-banner-${b.id}`} />}
                    <Badge className="bg-black/60 text-white backdrop-blur">{b.aspect_ratio}</Badge>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <h3 className="truncate font-display text-base font-semibold">{b.name}</h3>
                      <p className="font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">
                        {b.images?.length || 0} image{(b.images?.length || 0) > 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="flex-1"
                      onClick={() => navigate(`/banner/${b.id}`)}
                      data-testid={`edit-banner-${b.id}`}
                    >
                      <Pencil className="mr-1 h-3.5 w-3.5" /> Éditer
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1"
                      disabled={!b.images?.length}
                      onClick={() => navigate(`/play/${b.id}`)}
                      data-testid={`play-banner-${b.id}`}
                    >
                      <Play className="mr-1 h-3.5 w-3.5" /> Lire
                    </Button>
                    {isPrincipal && (
                      <Button size="sm" variant="secondary" onClick={() => duplicate(b)} title="Dupliquer"
                        data-testid={`duplicate-banner-${b.id}`}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {isPrincipal && (
                      <ProtectedDelete live={live.banner_id === b.id} kind="bandeau" name={b.name} onConfirm={() => remove(b.id)}
                        triggerTestId={`delete-banner-${b.id}`} confirmTestId={`confirm-delete-${b.id}`} />
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
